import React, { useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { Product, ApiResponse, ProductFormData } from '../types/api';

const Products: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: 0,
    stock_quantity: 0,
    vendor_id: null,
    category_id: null,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 30; // Number of products per page (match backend default)

  const { data: productsResponse, isLoading, refetch }: UseQueryResult<any> = useQuery({
    queryKey: ['products-real', page],
    queryFn: async () => {
              const res = await fetch(`https://api.erp.oroud.net/api/products.php?page=${page}&per_page=${pageSize}`);
      return res.json();
    },
    keepPreviousData: true,
  });

  // The real API returns { products: [...] }
  const products = productsResponse?.products || productsResponse?.data;

  React.useEffect(() => {
    const totalPagesFromApi = Number(productsResponse?.pagination?.total_pages);
    if (totalPagesFromApi && !isNaN(totalPagesFromApi)) {
      setTotalPages(totalPagesFromApi);
    } else {
      setTotalPages(1);
    }
  }, [productsResponse]);

  // Debug: log current page and products
  React.useEffect(() => {
    console.log('Current page:', page, 'Products:', products);
  }, [page, products]);

  const handleOpen = (product?: Product) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        stock_quantity: product.stock_quantity,
        vendor_id: product.vendor_id,
        category_id: product.category_id,
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        stock_quantity: 0,
        vendor_id: null,
        category_id: null,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedProduct(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = ['price', 'stock_quantity', 'vendor_id', 'category_id'];
    
    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? (value ? Number(value) : null) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedProduct) {
        await api.updateProduct(selectedProduct.id, formData);
      } else {
        await api.createProduct(formData);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleDelete = async (productId: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.deleteProduct(productId);
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Products</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Product
        </Button>
      </Box>

      <Grid container spacing={3}>
        {Array.isArray(products) && products.length > 0 ? (
          products.map((product: any) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {Array.isArray(product.images) && product.images.length > 0 && product.images[0]?.src ? (
                  <CardMedia
                    component="img"
                    height="180"
                    image={product.images[0].src}
                    alt={product.name}
                    sx={{ objectFit: 'contain', background: '#fafafa', borderBottom: '1px solid #eee' }}
                  />
                ) : null}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>{product.name}</Typography>
                  <Typography variant="subtitle1" color="primary" fontWeight={600} mb={1}>
                    {product.price} MAD
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vendor: {product.store?.shop_name || product.vendor_name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Category: {product.categories && product.categories.length > 0 ? product.categories[0].name : 'N/A'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary" onClick={() => handleOpen(product)} startIcon={<EditIcon />}>Edit</Button>
                  <Button size="small" color="error" onClick={() => handleDelete(product.id)} startIcon={<DeleteIcon />}>Delete</Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}><Typography>No products found.</Typography></Grid>
        )}
      </Grid>
      <Box display="flex" justifyContent="center" mt={4}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
          disabled={totalPages <= 1}
        />
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Price"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Stock Quantity"
              name="stock_quantity"
              type="number"
              value={formData.stock_quantity}
              onChange={handleInputChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Vendor ID"
              name="vendor_id"
              type="number"
              value={formData.vendor_id ?? ''}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Category ID"
              name="category_id"
              type="number"
              value={formData.category_id ?? ''}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedProduct ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Products;